/**
 * 投放词库 Hook
 * 限制: <160行
 */
import { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { targetingKeywordApi } from './api';
import { TargetingKeyword, KeywordLibraryStats, Marketplace } from './types';


export const useTargetingKeywords = (skuId?: number, marketplace?: Marketplace) => {
    const [keywords, setKeywords] = useState<TargetingKeyword[]>([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<KeywordLibraryStats | null>(null);
    const { message } = App.useApp();

    const fetchKeywords = useCallback(async () => {
        if (!skuId) {
            setKeywords([]);
            setStats(null);
            return;
        }

        try {
            setLoading(true);
            const [keywordsData, statsData] = await Promise.all([
                targetingKeywordApi.list({ sku_id: skuId, marketplace }),
                targetingKeywordApi.stats({ sku_id: skuId, marketplace }),
            ]);
            setKeywords(Array.isArray(keywordsData) ? keywordsData : []);
            setStats(statsData);
        } catch (error) {
            console.error('获取投放词库失败:', error);
            setKeywords([]);
            message.error('获取投放词库失败');
        } finally {
            setLoading(false);
        }
    }, [skuId, marketplace, message]);

    useEffect(() => {
        fetchKeywords();
    }, [fetchKeywords]);

    const addKeyword = async (data: Partial<TargetingKeyword>) => {
        try {
            const newKeyword = await targetingKeywordApi.create({
                ...data,
                sku: skuId,
                marketplace: marketplace || 'US',
            });
            setKeywords(prev => [newKeyword, ...prev]);
            message.success('添加成功');
            return newKeyword;
        } catch (error) {
            console.error('添加关键词失败:', error);
            message.error('添加失败');
            throw error;
        }
    };

    const updateKeyword = async (id: number, data: Partial<TargetingKeyword>) => {
        try {
            const updated = await targetingKeywordApi.update(id, data);
            setKeywords(prev => prev.map(k => k.id === id ? updated : k));
            message.success('更新成功');
            return updated;
        } catch (error) {
            console.error('更新关键词失败:', error);
            message.error('更新失败');
            throw error;
        }
    };

    const deleteKeyword = async (id: number) => {
        try {
            await targetingKeywordApi.delete(id);
            setKeywords(prev => prev.filter(k => k.id !== id));
            message.success('删除成功');
        } catch (error) {
            console.error('删除关键词失败:', error);
            message.error('删除失败');
            throw error;
        }
    };

    const bulkImport = async (keywordsData: Array<{
        keyword: string;
        keyword_type?: string;
        match_type?: string;
        conversion_rate?: number;
        priority?: number;
    }>) => {
        if (!skuId || !marketplace) return;

        try {
            const result = await targetingKeywordApi.bulkImport(skuId, marketplace, keywordsData);
            message.success(`导入成功: 新增 ${result.created}, 更新 ${result.updated}`);
            await fetchKeywords();
            return result;
        } catch (error) {
            console.error('批量导入失败:', error);
            message.error('批量导入失败');
            throw error;
        }
    };

    const batchUpdateStatus = async (ids: number[], isActive: boolean) => {
        try {
            await targetingKeywordApi.batchUpdateStatus(ids, isActive);
            setKeywords(prev => prev.map(k =>
                ids.includes(k.id) ? { ...k, is_active: isActive } : k
            ));
            message.success(`已${isActive ? '启用' : '停用'} ${ids.length} 个关键词`);
        } catch (error) {
            console.error('批量更新状态失败:', error);
            message.error('操作失败');
            throw error;
        }
    };

    const batchDelete = async (ids: number[]) => {
        try {
            await targetingKeywordApi.batchDelete(ids);
            setKeywords(prev => prev.filter(k => !ids.includes(k.id)));
            message.success(`已删除 ${ids.length} 个关键词`);
        } catch (error) {
            console.error('批量删除失败:', error);
            message.error('删除失败');
            throw error;
        }
    };

    return {
        keywords,
        loading,
        stats,
        refresh: fetchKeywords,
        addKeyword,
        updateKeyword,
        deleteKeyword,
        bulkImport,
        batchUpdateStatus,
        batchDelete,
    };
};
